import { useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import Skeleton, { SkeletonTheme } from 'react-loading-skeleton'
import { ReactSVG } from 'react-svg'

import { useCartInfoStore, useCartStore } from '@entities/Cart'

import { GoToCheckout } from '@features/go-to-checkout'
import { useDelivery } from '@features/shipping-methods'

import { DiscountControl } from '@widgets/discount-control'

import { useZonesFromAddress } from '@shared/hooks/useZonesFromAddress'
import { computeCartTotalsBreakdown } from '@shared/lib/cartTotals'
import { cn } from '@shared/lib/cn'
import Button from '@shared/ui/button'
import { formatPrice } from '@shared/uttils'
import { formatGramsToHumanWeight } from '@shared/uttils/formatGramsToHumanWeight'
import getNoun from '@shared/uttils/getNoun'

import styles from './CartTotal.module.scss'

export default function CartTotal () {
  const { t: tp } = useTranslation('product')
  const { t: tc } = useTranslation('cart')
  const { t: tco } = useTranslation('common')
  const { t: to } = useTranslation('order')
  const { getTotalQuantity } = useCartStore()
  const data = useCartInfoStore((state) => state.cartInfo)
  const loading = useCartInfoStore((state) => state.cartInfoLoading)

  const { deliveryType, maxSlotWeight, maxExpressWeight } = useDelivery()

  const {
    minOrderCost,
    isPickup,
    freeSlotDeliveryCostFrom,
    deliveryCost,
    loading: zonesLoading,
  } = useZonesFromAddress()

  const [ showDeliveryList, setShowDeliveryList ] = useState(false)

  const total = Number(data?.totals?.total ?? 0)

  const cartWeight = useMemo(() => Number(data?.totalWeight ?? 0), [ data?.totalWeight ])
  const weightLimit = useMemo(() => {
    return deliveryType === 'express' ? maxExpressWeight : maxSlotWeight
  }, [ deliveryType, maxExpressWeight, maxSlotWeight ])
  const weightExceeded = useMemo(() => {
    if (!weightLimit || weightLimit <= 0) return false
    return cartWeight > weightLimit
  }, [ cartWeight, weightLimit ])

  const {
    deliveryTotal,
    deliverySale,
    orderTotal,
    couponSale,
    bonusSale,
    bonusAmount,
    promocodeSale,
    cashbackAmount,
    loyaltyDiscount,
    hasSalesBreakdown,
  } = computeCartTotalsBreakdown(data, isPickup())

  return (
    <div className={styles.root}>
      <div className={cn(styles.root__row, styles.head)}>
        {data && (
          <h3 className={styles.root__title}>
            {getTotalQuantity()}{' '}
            {getNoun(
              getTotalQuantity(),
              tp('product.1'),
              tp('product.2'),
              tp('product.3'),
            )}
          </h3>
        )}

        {!!data?.totalWeight && (
          <p className={styles.root__weight}>
            ~{' '}
            {formatGramsToHumanWeight({
              weight: Number(data?.totalWeight),
              t: tco,
            })}
          </p>
        )}
      </div>

      {/* 1. Товары - базовая стоимость */}
      {(data?.totals?.baseTotal ?? 0) > 0 && (
        <div className={styles.root__info}>
          <div className={styles.root__row}>
            <h4 className={styles.root__subtitle}>{tc('total.products')}: </h4>
            <p className={styles.root__value}>
              {formatPrice(Number(data?.totals?.baseTotal ?? 0))}
            </p>
          </div>
        </div>
      )}

      {/* 2. Скидка на товары (желтая цена) */}
      {(data?.totals?.baseSale ?? 0) > 0 && (
        <div className={styles.root__info}>
          <div className={styles.root__row}>
            <h4 className={styles.root__subtitle}>{tc('total.discountOnProducts')}: </h4>
            <p className={cn(styles.root__value, styles['root__value--sale'])}>
              -{formatPrice(Number(data?.totals?.baseSale ?? 0))}
            </p>
          </div>
        </div>
      )}

      {/* 4. Доставка */}
      {!isPickup() && deliveryTotal > 0 && (
        <div className={styles.root__info}>
          <div className={styles.root__row}>
            <h4 className={styles.root__subtitle}>{tc('total.delivery')}: </h4>
            <p className={styles.root__value}>
              {formatPrice(deliveryTotal - deliverySale)}
            </p>
          </div>
          {deliverySale > 0 && (
            <div className={styles.root__row}>
              <h4 className={styles.root__subtitle}>{tc('total.discount')}: </h4>
              <p className={cn(styles.root__value, styles['root__value--sale'])}>
                -{formatPrice(deliverySale)}
              </p>
            </div>
          )}
        </div>
      )}

      <DiscountControl />

      {/* 3. Промокод/бонусы/лояльность (после DiscountControl) */}
      {hasSalesBreakdown ? (
        <>
          {bonusSale > 0 && (
            <div className={styles.root__info}>
              <div className={styles.root__row}>
                <h4 className={styles.root__subtitle}>{tc('total.bonusCharge')}: </h4>
                <p className={cn(styles.root__value, styles['root__value--sale'])}>
                  -{tc('total.bonusCount', { count: Math.floor(bonusAmount) })}
                </p>
              </div>
            </div>
          )}

          {couponSale > 0 && (
            <div className={styles.root__info}>
              <div className={styles.root__row}>
                <h4 className={styles.root__subtitle}>{tc('total.couponUsed')}: </h4>
                <p className={cn(styles.root__value, styles['root__value--sale'])}>
                  -{formatPrice(couponSale)}
                </p>
              </div>
            </div>
          )}

          {promocodeSale > 0 && (
            <div className={styles.root__info}>
              <div className={styles.root__row}>
                <h4 className={styles.root__subtitle}>{tc('total.promocodeUsed')}: </h4>
                <p className={cn(styles.root__value, styles['root__value--sale'])}>
                  -{formatPrice(promocodeSale)}
                </p>
              </div>
            </div>
          )}

          {loyaltyDiscount > 0 && (
            <div className={styles.root__info}>
              <div className={styles.root__row}>
                <h4 className={styles.root__subtitle}>{tc('total.loyaltyDiscount')}: </h4>
                <p className={cn(styles.root__value, styles['root__value--sale'])}>
                  -{formatPrice(loyaltyDiscount)}
                </p>
              </div>
            </div>
          )}

          {cashbackAmount > 0 && (
            <div className={styles.root__info}>
              <div className={styles.root__row}>
                <h4 className={styles.root__subtitle}>{tc('total.cashbackWillBeCredited')}: </h4>
                <p className={cn(styles.root__value, styles['root__value--bonus'])}>
                  +{tc('total.bonusCount', { count: Math.floor(cashbackAmount) })}
                </p>
              </div>
            </div>
          )}
        </>
      ) : (data?.totals?.sale ?? 0) > 0 && (
        <div className={styles.root__info}>
          <div className={styles.root__row}>
            <h4 className={styles.root__subtitle}>
              {data?.promocode ? tc('total.promocode') : tc('total.discount')}:{' '}
            </h4>
            <p className={cn(styles.root__value, styles['root__value--sale'])}>
              -{formatPrice(Number(data?.totals?.sale ?? 0))}
            </p>
          </div>
        </div>
      )}

      {/* 5. Итого */}
      <div className={styles.root__info}>
        <div className={styles.root__row}>
          <h4 className={styles.root__subtitle}>{tc('total.total')}: </h4>
          <p className={cn(styles.root__value, styles['root__value--total'])}>
            {formatPrice(orderTotal)}
          </p>
        </div>
      </div>

      <div className={styles['root__buttons']}>
        {(loading && !data) || zonesLoading ? (
          <SkeletonTheme>
            <Skeleton
              count={1}
              width="100%"
              height={56}
              style={{
                borderRadius: 12,
              }}
            />
          </SkeletonTheme>
        ) : weightExceeded ? (
          <Button disabled size="large">
            {to('weight_exceeded', {
              maxWeight: formatGramsToHumanWeight({
                weight: weightLimit || 0,
                t: tco,
              }),
            })}
          </Button>
        ) : !isPickup() ? (
          total < minOrderCost ? (
            <Button disabled size="large">
              {`${tc('order.min_limit')} ${formatPrice(minOrderCost)}. ${tc('order.missing_amount')} ${formatPrice(minOrderCost - total)}`}
            </Button>
          ) : (
            <GoToCheckout />
          )
        ) : (
          <GoToCheckout />
        )}
        {(loading && !data) || zonesLoading ? (
          <SkeletonTheme>
            <Skeleton
              count={1}
              width="100%"
              height={56}
              style={{
                borderRadius: 12,
              }}
            />
          </SkeletonTheme>
        ) : !isPickup() && freeSlotDeliveryCostFrom > 0 ? (
          <div
            className={styles['root__delivery-cost']}
            onClick={() => setShowDeliveryList((prev) => !prev)}
          >
            <ReactSVG
              className={styles['root__delivery-cost__icon']}
              src="/icons/moped.svg"
              wrapper="span"
            />
            <span className={styles['root__delivery-cost__text']}>
              {/* // ? `${formatPrice(freeSlotDeliveryCostFrom - total)} до бесплатной доставки` */}
              {total < freeSlotDeliveryCostFrom
                ? tc('delivery.amount_to_free', {
                  amount: formatPrice(freeSlotDeliveryCostFrom - total),
                })
                : tc('delivery.free')}
            </span>
            <ReactSVG className={styles.control} src="/icons/arrow-right.svg" />
            {showDeliveryList && (
              <div className={styles['root__delivery-list']}>
                <p>
                  {/* Заказ от {formatPrice(minOrderCost)}:
                  <span>доставка {formatPrice(deliveryCost)}</span> */}
                  {tc('delivery.conditions.under_min', {
                    minOrderCost: formatPrice(minOrderCost),
                    deliveryCost: formatPrice(deliveryCost),
                  })}
                </p>
                <p>
                  {/* Заказ от {formatPrice(freeSlotDeliveryCostFrom)}:{" "}
                  <span>доставка бесплатная</span> */}
                  {tc('delivery.conditions.over_free', {
                    freeSlotDeliveryCostFrom: formatPrice(
                      freeSlotDeliveryCostFrom,
                    ),
                  })}
                </p>
              </div>
            )}
          </div>
        ) : null}
      </div>
    </div>
  )
}
